import {
  powerUpJsonObjectSchema,
  type BoardPowerUp,
  type PowerUpAuthorizationState,
  type PowerUpJsonValue,
} from '@molt/shared';
import { PlugZap, Save, Settings, Trash2 } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';

import { Button, Dialog, EmptyState, Notice, Skeleton } from '@/components';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

/**
 * ⚠ THE SENTINEL-SPLIT IDIOM, set here and reused by `EnterprisePowerUpAdmin`.
 *
 * The disable confirmation emphasises the Power-Up's name inside a sentence,
 * wrapped in a `<strong>`. Three designs were weighed and two rejected: TWO
 * FRAGMENT KEYS (a prefix and a suffix) is not translatable — a join fixes word
 * order and the separator, the `POLICY_FIELD_LABELS` class of defect; and ONE
 * KEY WITHOUT THE `<strong>` removes emphasis from a DESTRUCTIVE confirmation,
 * which the product tie-breaker does not trade for tidiness.
 *
 * Taken: one whole-sentence key with a `{powerUp}` slot, resolved with a
 * codepoint no translation can contain and split on it at render. A locale
 * keeps full freedom to move the slot anywhere in the sentence.
 *
 * ⚠ THE FALLBACK ARM IS NOT OPTIONAL. A translation that omits `{powerUp}`
 * yields one part, and rendering `parts[0]` alone would silently DELETE the
 * Power-Up's name from a destructive dialog. The plain arm degrades it to a
 * correct sentence instead.
 *
 * `BoardDangerConfirm.tsx:92`, `:104` and `WorkspaceDeleteDialog.tsx:58` hold
 * the same construct and are still unrouted. Copy this; do not re-decide it.
 */
const NAME_SENTINEL = String.fromCharCode(0);

type PowerUpSettingsValue = Record<string, PowerUpJsonValue>;

export interface BoardPowerUpListProps {
  readonly entries: readonly BoardPowerUp[];
  readonly loading?: boolean | undefined;
  readonly error?: string | undefined;
  readonly canManage: boolean;
  readonly manageDisabledReason: string | null;
  readonly visibilityBusyId?: string | null | undefined;
  readonly settingsBusyId?: string | null | undefined;
  readonly authorizationBusyId?: string | null | undefined;
  readonly disableBusyId?: string | null | undefined;
  readonly personalSettingsBusyId?: string | null | undefined;
  readonly rowErrors?: Readonly<Record<string, string>> | undefined;
  readonly onToggleHeaderVisibility: (boardPowerUpId: string, visible: boolean) => void;
  readonly onSaveSettings: (
    boardPowerUpId: string,
    settings: PowerUpSettingsValue | null,
  ) => void;
  readonly onAuthorize: (boardPowerUpId: string) => void;
  readonly onRevokeAuthorization: (boardPowerUpId: string) => void;
  readonly onDisable: (boardPowerUpId: string, keepData: boolean) => void;
  readonly onRemovePersonalSettings: (boardPowerUpId: string) => void;
  readonly onRetry: () => void;
}

function displayName(t: InterfaceTranslator, entry: BoardPowerUp): string {
  const name = entry.powerUp.name.trim();
  return name.length === 0 ? t('powerUps.untitledName') : name;
}

function settingsText(settings: PowerUpSettingsValue | null): string {
  return settings === null ? '' : JSON.stringify(settings, null, 2);
}

/**
 * ⚠ A CHOICE OF KEY, not a verb stem slotted into one sentence. `Reconnect` and
 * `Authorise` are whole imperatives here; either as a `{verb}` slot would fix
 * word order and fix that exactly one word is needed.
 */
function authorizationLabel(
  t: InterfaceTranslator,
  name: string,
  state: PowerUpAuthorizationState,
): string {
  if (state === 'reconnect_required') {
    return t('powerUps.board.reconnect', { powerUp: name });
  }
  return t('powerUps.board.authorise', { powerUp: name });
}

interface BoardPowerUpRowProps {
  readonly entry: BoardPowerUp;
  readonly canManage: boolean;
  readonly manageDisabledReason: string | null;
  readonly visibilityBusy: boolean;
  readonly settingsBusy: boolean;
  readonly authorizationBusy: boolean;
  readonly disableBusy: boolean;
  readonly personalSettingsBusy: boolean;
  readonly rowError?: string | undefined;
  readonly onToggleHeaderVisibility: (boardPowerUpId: string, visible: boolean) => void;
  readonly onSaveSettings: (
    boardPowerUpId: string,
    settings: PowerUpSettingsValue | null,
  ) => void;
  readonly onAuthorize: (boardPowerUpId: string) => void;
  readonly onRevokeAuthorization: (boardPowerUpId: string) => void;
  readonly onDisable: (boardPowerUpId: string, keepData: boolean) => void;
  readonly onRemovePersonalSettings: (boardPowerUpId: string) => void;
}

function BoardPowerUpRow({
  entry,
  canManage,
  manageDisabledReason,
  visibilityBusy,
  settingsBusy,
  authorizationBusy,
  disableBusy,
  personalSettingsBusy,
  rowError,
  onToggleHeaderVisibility,
  onSaveSettings,
  onAuthorize,
  onRevokeAuthorization,
  onDisable,
  onRemovePersonalSettings,
}: BoardPowerUpRowProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const name = displayName(t, entry);
  const [settingsDraft, setSettingsDraft] = useState(settingsText(entry.settings));
  /**
   * ⚠ HOLDS A CATALOGUE KEY, NOT ENGLISH. State set inside an event handler has
   * the same problem a module-level constant has — no context to call `t` from
   * at the moment it is written — one step later. The render resolves it.
   */
  const [settingsErrorKey, setSettingsErrorKey] = useState<string | null>(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [keepData, setKeepData] = useState(false);
  const cancelDisableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setSettingsDraft(settingsText(entry.settings));
    setSettingsErrorKey(null);
  }, [entry.settings]);

  function handleVisibilityChange(event: ChangeEvent<HTMLInputElement>): void {
    onToggleHeaderVisibility(entry.id, event.currentTarget.checked);
  }

  function handleSettingsSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = settingsDraft.trim();
    if (trimmed === '') {
      setSettingsErrorKey(null);
      onSaveSettings(entry.id, null);
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      setSettingsErrorKey('powerUps.board.settingsInvalidJson');
      return;
    }

    const result = powerUpJsonObjectSchema.safeParse(parsed);
    if (!result.success) {
      setSettingsErrorKey('powerUps.board.settingsInvalidShape');
      return;
    }

    setSettingsErrorKey(null);
    onSaveSettings(entry.id, result.data);
  }

  const hasSettings = entry.powerUp.manifest.settings !== null;
  const authButtonLabel = authorizationLabel(t, name, entry.authorizationState);
  const canDisable = canManage && !disableBusy;
  const disableBodyParts = t('powerUps.board.disableBody', {
    powerUp: NAME_SENTINEL,
  }).split(NAME_SENTINEL);

  return (
    <article
      aria-label={t('powerUps.board.rowLabel', { powerUp: name })}
      className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3 shadow-card"
    >
      <div className="flex items-start gap-3">
        {entry.powerUp.iconUrl === null ? (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-card bg-surface text-accent">
            <PlugZap aria-hidden="true" className="size-5" />
          </span>
        ) : (
          <img
            src={entry.powerUp.iconUrl}
            alt=""
            className="size-10 shrink-0 rounded-card bg-surface object-contain"
          />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-ink">{name}</h3>
          {/* ⚠ `entry.enabledAt` is a RAW POSTGRES TIMESTAMP
              (`2026-08-27 10:00:00.000+00`). The FRAME is routed and the value
              stays literal; formatting it through `lib/locale-formatting` is a
              behaviour change outside a routing slice. Reported, not taken. */}
          <p className="text-xs text-ink-muted">
            {t('powerUps.board.enabledAt', { date: entry.enabledAt })}
          </p>
        </div>
      </div>

      <label
        className={cn(
          'flex items-center gap-2 rounded-card border border-border bg-surface px-3 py-2 text-sm font-medium text-ink',
          !canManage && 'text-ink-muted',
        )}
      >
        <input
          type="checkbox"
          checked={entry.headerButtonVisible}
          disabled={!canManage || visibilityBusy}
          onChange={handleVisibilityChange}
        />
        {t('powerUps.board.headerVisibility', { powerUp: name })}
      </label>
      {!canManage && manageDisabledReason !== null ? (
        <p className="text-xs text-ink-muted">{manageDisabledReason}</p>
      ) : null}

      {hasSettings ? (
        <form className="flex flex-col gap-2" onSubmit={handleSettingsSubmit}>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('powerUps.board.settingsLabel', { powerUp: name })}
            <textarea
              value={settingsDraft}
              onChange={(event) => setSettingsDraft(event.currentTarget.value)}
              disabled={!canManage || settingsBusy}
              rows={4}
              className="min-h-24 resize-y rounded-card border border-border-strong bg-surface px-3 py-2 font-mono text-xs text-ink"
            />
          </label>
          {settingsErrorKey === null ? null : (
            <p role="alert" className="text-sm text-bad">
              {t(settingsErrorKey)}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              disabled={!canManage || settingsBusy}
              iconStart={<Save aria-hidden="true" className="size-4" />}
            >
              {t('powerUps.board.saveSettings', { powerUp: name })}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="quiet"
              disabled={!canManage || settingsBusy}
              onClick={() => {
                setSettingsDraft('');
                setSettingsErrorKey(null);
                onSaveSettings(entry.id, null);
              }}
            >
              {t('powerUps.board.clearSettings', { powerUp: name })}
            </Button>
          </div>
        </form>
      ) : (
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Settings aria-hidden="true" className="size-4" />
          {t('powerUps.board.noSettings')}
        </p>
      )}

      <div className="rounded-card border border-border bg-surface px-3 py-2 text-sm">
        {entry.authorizationState === 'not_required' ? (
          <p className="text-ink-muted">{t('powerUps.board.authNotRequired')}</p>
        ) : entry.authorizationState === 'authorized' ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ok">{t('powerUps.board.connected')}</span>
            <Button
              type="button"
              size="sm"
              variant="quiet"
              disabled={authorizationBusy}
              onClick={() => onRevokeAuthorization(entry.id)}
            >
              {t('powerUps.board.disconnect', { powerUp: name })}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-ink-muted">
              {entry.authorizationState === 'reconnect_required'
                ? t('powerUps.board.reconnectBody')
                : t('powerUps.board.connectBody')}
            </p>
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={authorizationBusy}
              onClick={() => onAuthorize(entry.id)}
            >
              {authButtonLabel}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-card border border-border bg-surface px-3 py-2 text-sm">
        <div className="flex flex-col gap-2">
          <p className="text-ink-muted">{t('powerUps.board.removalBody')}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={!canDisable}
              disabledReason={canManage ? undefined : (manageDisabledReason ?? undefined)}
              iconStart={<Trash2 aria-hidden="true" className="size-4" />}
              onClick={() => {
                setKeepData(false);
                setDisableDialogOpen(true);
              }}
            >
              {t('powerUps.board.disable', { powerUp: name })}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="quiet"
              busy={personalSettingsBusy}
              busyLabel={t('powerUps.board.removePersonalBusy', { powerUp: name })}
              onClick={() => onRemovePersonalSettings(entry.id)}
            >
              {t('powerUps.board.removePersonal', { powerUp: name })}
            </Button>
          </div>
        </div>
      </div>

      {rowError === undefined ? null : (
        <p role="alert" className="text-sm text-bad">
          {rowError}
        </p>
      )}

      <Dialog
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        title={t('powerUps.board.disableTitle', { powerUp: name })}
        description={t('powerUps.board.disableDescription')}
        size="sm"
        initialFocus={cancelDisableRef}
        footer={
          <>
            <Button
              type="button"
              variant="danger"
              busy={disableBusy}
              busyLabel={t('powerUps.board.disableBusy', { powerUp: name })}
              onClick={() => {
                onDisable(entry.id, keepData);
                setDisableDialogOpen(false);
              }}
            >
              {t('powerUps.board.disable', { powerUp: name })}
            </Button>
            <Button
              ref={cancelDisableRef}
              type="button"
              variant="quiet"
              onClick={() => setDisableDialogOpen(false)}
            >
              {t('powerUps.cancel')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-ink-muted">
          {disableBodyParts.length === 2 ? (
            <p>
              {disableBodyParts[0]}
              <strong className="font-medium text-ink">{name}</strong>
              {disableBodyParts[1]}
            </p>
          ) : (
            <p>{t('powerUps.board.disableBody', { powerUp: name })}</p>
          )}
          <label className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 font-medium text-ink">
            <input
              type="checkbox"
              checked={keepData}
              onChange={(event) => setKeepData(event.currentTarget.checked)}
            />
            <span>
              {t('powerUps.keepData')}
              <span className="block font-normal text-ink-muted">
                {t('powerUps.board.keepDataHint')}
              </span>
            </span>
          </label>
        </div>
      </Dialog>
    </article>
  );
}

export function BoardPowerUpList({
  entries,
  loading = false,
  error,
  canManage,
  manageDisabledReason,
  visibilityBusyId,
  settingsBusyId,
  authorizationBusyId,
  disableBusyId,
  personalSettingsBusyId,
  rowErrors = {},
  onToggleHeaderVisibility,
  onSaveSettings,
  onAuthorize,
  onRevokeAuthorization,
  onDisable,
  onRemovePersonalSettings,
  onRetry,
}: BoardPowerUpListProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <section aria-label={t('powerUps.board.heading')} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-lg font-semibold text-ink">
          {t('powerUps.board.heading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('powerUps.board.description')}</p>
      </div>

      {error === undefined ? null : (
        <Notice
          tone="bad"
          title={t('powerUps.board.loadFailedTitle')}
          body={error}
          action={
            <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
              {t('powerUps.retry')}
            </Button>
          }
        />
      )}

      {loading ? (
        <div aria-busy="true" className="grid grid-cols-1 gap-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} shape="block" className="h-64" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          tone="neutral"
          headingLevel={3}
          title={t('powerUps.board.emptyTitle')}
          body={t('powerUps.board.emptyBody')}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {entries.map((entry) => (
            <BoardPowerUpRow
              key={entry.id}
              entry={entry}
              canManage={canManage}
              manageDisabledReason={manageDisabledReason}
              visibilityBusy={visibilityBusyId === entry.id}
              settingsBusy={settingsBusyId === entry.id}
              authorizationBusy={authorizationBusyId === entry.id}
              disableBusy={disableBusyId === entry.id}
              personalSettingsBusy={personalSettingsBusyId === entry.id}
              rowError={rowErrors[entry.id]}
              onToggleHeaderVisibility={onToggleHeaderVisibility}
              onSaveSettings={onSaveSettings}
              onAuthorize={onAuthorize}
              onRevokeAuthorization={onRevokeAuthorization}
              onDisable={onDisable}
              onRemovePersonalSettings={onRemovePersonalSettings}
            />
          ))}
        </div>
      )}
    </section>
  );
}
